const BUILD_ID_STORAGE_KEY = 'orbit:build-id'

export async function clearOrbitRuntimeCaches(): Promise<void> {
  if (typeof window === 'undefined') {
    return
  }

  if ('caches' in window) {
    const cacheKeys = await window.caches.keys()
    await Promise.all(cacheKeys.map((key) => window.caches.delete(key)))
  }

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((registration) => registration.unregister()))
  }
}

/**
 * On each app boot, check whether the build has changed.
 * - index.html is served with no-cache so the browser always fetches the
 *   latest HTML, which references content-hashed JS/CSS bundles.
 * - Therefore the NEW code is already running by the time this function
 *   executes — no reload is needed.
 * - We still clear the Cache API and service workers defensively, in case
 *   a future iteration adds offline support.
 */
export async function refreshCacheForNewBuild(): Promise<void> {
  if (typeof window === 'undefined') {
    return
  }

  // Strip any leftover ?v= cache-bust param from the URL bar (no reload)
  const currentUrl = new URL(window.location.href)
  if (currentUrl.searchParams.has('v')) {
    currentUrl.searchParams.delete('v')
    window.history.replaceState(null, '', currentUrl.toString())
  }

  const currentBuildId = __APP_BUILD_ID__
  const previousBuildId = localStorage.getItem(BUILD_ID_STORAGE_KEY)

  // First visit — store the build ID and move on
  if (!previousBuildId) {
    localStorage.setItem(BUILD_ID_STORAGE_KEY, currentBuildId)
    return
  }

  // Same build — nothing to do
  if (previousBuildId === currentBuildId) {
    return
  }

  // New build detected — clear stale caches, update stored build ID
  localStorage.setItem(BUILD_ID_STORAGE_KEY, currentBuildId)
  await clearOrbitRuntimeCaches()
  // No reload needed: index.html is no-cache so the new JS bundle is already
  // loaded and executing. Content-hashed asset filenames guarantee no stale
  // JS/CSS is referenced.
}
